import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Divider,
  Image,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import { ExclamationCircleOutlined, PrinterOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";

import {
  HEADERS_EXPORT_DETAIL,
  POST_LOAD_BILL_SALE_IMPOUND,
  POST_SALE_IMPOUND,
} from "../../../API/apiUrls";
import DateCustom from "../../../../hook/DateCustom";
import CurrencyFormat from "../../../../hook/CurrencyFormat";
import ExportPDF from "../report/ExportPDF";
const { Text, Title } = Typography;

const InfoRow = ({ label, value, valueColor, strong = false }) => (
  <div style={{ marginBottom: 12 }}>
    <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
      {label}
    </Text>
    <Text
      strong={strong}
      style={{ fontSize: 14, color: valueColor, wordBreak: "break-word" }}
    >
      {value ?? "-"}
    </Text>
  </div>
);

const formatPhone = (p) => {
  const d = String(p ?? "").replace(/\D/g, "");
  return d.length === 10
    ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`
    : p || "-";
};

const formatIdCard = (id) => {
  const d = String(id ?? "").replace(/\D/g, "");
  return d.length === 13
    ? `${d[0]}-${d.slice(1, 5)}-${d.slice(5, 10)}-${d.slice(10, 12)}-${d[12]}`
    : id || "-";
};

// strip leading invisible tone marks (เช่น "่่่JS1C…")
const cleanChassis = (s) =>
  String(s ?? "")
    .replace(/^[\u0e30-\u0e4e]+/, "")
    .trim() || "-";

// ─── Main Modal ───────────────────────────────────────────────────
const SellingCarImpound = ({ open, close, record, handleLoad }) => {
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const roleId = localStorage.getItem("ROLE_ID");
  const [
    currencyFormat,
    currencyFormatComma,
    currencyFormatPoint,
    currencyFormatNoPoint,
  ] = CurrencyFormat();
  const [convertDateThai, convertDateThaiShort] = DateCustom();
  const [loading, setLoading] = useState(false);
  const [loadDetailData, setLoadDetailData] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paymentType, setPaymentType] = useState("01");
  const [getPAYCODE, setGetPAYCODE] = useState([]);

  const loadData = useCallback(
    async (signal) => {
      if (!record?.STRNO) return;

      setLoading(true);
      try {
        const res = await axios.post(
          POST_LOAD_BILL_SALE_IMPOUND,
          { STRNO: record.STRNO },
          { signal },
        );

        if (!res?.data) {
          message.warning("ไม่พบข้อมูล");
          setLoadDetailData([]);
          return;
        }
        setLoadDetailData(res.data.data?.[0] ?? []);
      } catch (error) {
        if (axios.isCancel(error) || error.name === "CanceledError") return;
        console.error("loadData error:", error);
        message.error(
          error.response?.data?.message || error.message || "เกิดข้อผิดพลาด",
        );
      } finally {
        setLoading(false);
      }
    },
    [record?.CONTNO, record?.newContno],
  );

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    loadData(controller.signal);

    return () => controller.abort();
  }, [open, loadData]);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      setLoading(true);

      try {
        const response = await axios.get(
          "https://api-payment-1024031113167.asia-southeast1.run.app/api-payment/get-paytype",
        );

        // 2. เช็คสถานะก่อนอัปเดต State
        if (isMounted) {
          if (response?.data && Array.isArray(response.data)) {
            setGetPAYCODE(response.data);
          } else {
            message.error({
              content: (
                <span style={{ fontSize: "18px" }}>
                  รูปแบบข้อมูลไม่ถูกต้อง หรือไม่พบข้อมูล
                </span>
              ),
              style: { marginTop: "10vh", fontSize: "20px", padding: "5px" },
              duration: 3, // แสดงนาน 3 วินาที
            });
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error in loadData: ", error);
          const errMsg =
            error.response?.data?.message ||
            "เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์";
          message.error(errMsg);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    // 3. Cleanup function: จะรันเมื่อ Component ถูกทำลาย
    return () => {
      isMounted = false;
    };
  }, []);

  const handleClose = () => {
    close(false);
  };

  const handleSave = async () => {
    try {
      const dataStatus = {
        idImpound: loadDetailData.id_impound,
        lawsuitId: record.lawsuitId,
        loanId: loadDetailData.loanId,
        idJudge: loadDetailData.idJudge,
        idJudgeDef: loadDetailData.idJudgeDef,
        newContno: loadDetailData.newContno,
        CONTNO: loadDetailData.CONTNO,
        PAYTYP: paymentType,
        sold_out_price: loadDetailData.sold_out_price,
        doc_no: loadDetailData.invoice_no,
        ref: loadDetailData.ref,
        userId: userId,
      };
      console.log("dataStatus", dataStatus);

      const successPostSaveStatus = await postSaveStatus(dataStatus);

      if (successPostSaveStatus) {
        setSaving(true);
        message.success("บันทึกข้อมูลสำเร็จ");
      }
    } catch (err) {
      if (err?.errorFields) {
        message.warning("กรุณากรอกข้อมูลให้ครบ");
      } else if (!err?.handled) {
        // เฉพาะ error ที่ยังไม่ได้แสดง message (กัน toast ซ้ำ)
        console.error("save error:", err);
        message.error("ไม่สามารถบันทึกข้อมูลได้");
      }
    } finally {
    }
  };

  const postSaveStatus = async (payload) => {
    try {
      const res = await axios.post(POST_SALE_IMPOUND, payload, {
        headers: HEADERS_EXPORT_DETAIL,
      });
      if (res.status !== 200) throw new Error(`HTTP ${res.status}`);

      return true;
    } catch (error) {
      error.handled = true;
      throw error;
    }
  };

  // ── Print ─────────────────────────────────────────────────────
  const handlePrint = async () => {
    try {
      setPrinting(true);
      const pdfData = {
        company_name: loadDetailData.name,
        company_address: loadDetailData.address,
        company_taxid: loadDetailData.taxId,
        invoice_no: loadDetailData.doc_no,
        invoice_date: loadDetailData.invoice_date,
        cashbill_no: loadDetailData.doc_no,
        contract_no: loadDetailData?.contract_no,
        buyer_name: loadDetailData.buyer_name,
        buyer_address: loadDetailData.buyer_address,
        buyer_taxid: loadDetailData.buyer_taxid,
        brand: loadDetailData?.MORTGAGE?.TYPE,
        model: loadDetailData?.MORTGAGE?.MODEL,
        color: loadDetailData.COLOR,
        chassis_no: cleanChassis(loadDetailData?.chassis_no),
        engine_no: loadDetailData.ENGNO,
        sold_out_price: Number(loadDetailData.sold_out_price) || 0, // ตอนนี้ได้ค่าถูกแล้ว
        receiver_name: loadDetailData.receiver_name,
        is_vat: loadDetailData.is_vat,
      };
      const doc = await ExportPDF(
        pdfData,
        loadDetailData,
        loadDetailData.companyInvoice,
        loadDetailData.companyReceipt,
        record,
        roleId,
      );

      // เปิดใน tab ใหม่
      doc.output("dataurlnewwindow");
    } catch (err) {
      if (err?.errorFields) {
        message.warning("กรุณากรอกข้อมูลให้ครบ");
      } else {
        console.error("print error:", err);
        message.error("ไม่สามารถสร้าง PDF ได้");
      }
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: "#faad14" }} />
          <span>ขายรถยึด</span>
        </Space>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={880}
      destroyOnHidden
    >
      <Spin spinning={loading} size="large" tip="Loading...">
        <Divider>
          ข้อมูลสินค้า{" "}
          {loadDetailData?.BAAB?.includes("จักรยานยนต์") ? "🏍️" : "🚗"}
        </Divider>
        <Card>
          <Row gutter={[24, 16]}>
            {/* รูป + ทะเบียน */}
            <Col xs={24} md={8}>
              <Image
                src={loadDetailData?.img}
                alt="ภาพรถ"
                style={{
                  borderRadius: 8,
                  width: "100%",
                  aspectRatio: "4/3",
                  objectFit: "cover",
                }}
              />
              <div style={{ marginTop: 8, textAlign: "center" }}>
                <Space size={8} style={{ marginTop: 6 }}>
                  <Tag
                    color="gold"
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      padding: "2px 12px",
                      margin: 0,
                      textAlign: "center",
                      background: "#fff",
                    }}
                  >
                    {loadDetailData?.REGNO}
                    <br />
                    {loadDetailData?.DORECV}
                  </Tag>
                </Space>
              </div>
            </Col>

            <Col xs={24} md={16}>
              <Row gutter={[16, 0]}>
                <Col xs={12}>
                  <InfoRow
                    label="เลขสัญญา"
                    value={loadDetailData?.contract_no}
                    strong
                  />
                </Col>
                <Col xs={12}>
                  <InfoRow
                    label="รหัสสินค้า"
                    value={loadDetailData?.product_code}
                  />
                </Col>
                <Col xs={12}>
                  <InfoRow label="ยี่ห้อ" value={loadDetailData?.TYPE} />
                </Col>
                <Col xs={12}>
                  <InfoRow label="รุ่น" value={loadDetailData?.MODEL} />
                </Col>
                <Col xs={12}>
                  <InfoRow
                    label="เลขตัวถัง"
                    value={cleanChassis(loadDetailData?.STRNO)}
                  />
                </Col>
                <Col xs={12}>
                  <InfoRow label="เลขเครื่อง" value={loadDetailData?.ENGNO} />
                </Col>
                <Col xs={8}>
                  <InfoRow label="สี" value={loadDetailData?.COLOR} />
                </Col>
                <Col xs={8}>
                  <InfoRow
                    label="วันที่จดทะเบียน"
                    value={convertDateThaiShort(
                      loadDetailData?.registration_date,
                    )}
                  />
                </Col>
                <Col xs={8}>
                  <InfoRow
                    label="หมดอายุภาษี"
                    value={
                      loadDetailData?.tax_expiration_date &&
                      dayjs(loadDetailData?.tax_expiration_date).year() >= 2000
                        ? convertDateThaiShort(
                            loadDetailData?.tax_expiration_date,
                          )
                        : "-"
                    }
                  />
                </Col>
                <Col xs={8}>
                  <InfoRow
                    label="ปีที่ผลิต"
                    value={`${loadDetailData?.MANUYR}${loadDetailData?.MANUYR ? ` (${loadDetailData?.MANUYR - 543})` : "-"}`}
                  />
                </Col>
                <Col xs={8}>
                  <InfoRow label="ความจุเครื่อง" value={loadDetailData?.CC} />
                </Col>
                <Col xs={8}>
                  <InfoRow label="เลขไมล์" value={loadDetailData?.MILERT} />
                </Col>
              </Row>
            </Col>
          </Row>

          <Divider />

          {/* ─── Section 2: ข้อมูลการขาย ─── */}
          <Title level={5} style={{ color: "#efa420" }}>
            💰 ข้อมูลการขาย
          </Title>
          <Row gutter={[16, 0]}>
            <Col xs={12} md={6}>
              <InfoRow
                label="วันประมูล"
                value={convertDateThaiShort(loadDetailData?.date, true)}
              />
            </Col>
            <Col xs={12} md={6}>
              <InfoRow
                label="วันสิ้นสุด"
                value={convertDateThaiShort(
                  loadDetailData?.auction_due_datetime,
                  true,
                )}
              />
            </Col>
            <Col xs={12} md={6}>
              <InfoRow
                label="วันที่ขาย"
                value={convertDateThaiShort(loadDetailData?.sold_out_date)}
                valueColor="#52c41a"
                strong
              />
            </Col>
            <Col xs={12} md={6}>
              <InfoRow
                label="ราคาทุน"
                value={currencyFormatPoint(loadDetailData?.cost_price)}
              />
            </Col>
            <Col xs={12} md={6}>
              <InfoRow
                label="ราคาขาย"
                value={currencyFormatPoint(
                  loadDetailData?.sold_out_price / 1.07,
                )}
                valueColor="blue"
                strong
              />
            </Col>
            {(loadDetailData?.DATA_TYPE === "vsfhp" ||
              loadDetailData?.DATA_TYPE === "vsfhp-h") && (
              <Col xs={12} md={6}>
                <InfoRow
                  label="vat (7%)"
                  value={currencyFormatPoint(
                    loadDetailData?.sold_out_price -
                      loadDetailData?.sold_out_price / 1.07,
                  )}
                  valueColor="red"
                  strong
                />
              </Col>
            )}
            <Col xs={12} md={6}>
              <InfoRow
                label="ราคาสุทธิ"
                value={currencyFormatPoint(loadDetailData?.sold_out_price)}
                valueColor="#52c41a"
                strong
              />
            </Col>
          </Row>

          <Divider />

          {/* ─── Section 3: ผู้ซื้อ ─── */}
          <Title level={5} style={{ color: "#efa420" }}>
            👤 ผู้ซื้อ
          </Title>
          <Row gutter={[16, 0]}>
            <Col xs={24}>
              <InfoRow
                label="ชื่อ / บริษัท"
                value={loadDetailData?.buyer_name}
                strong
              />
            </Col>
            <Col xs={24} md={12}>
              <InfoRow
                label="เลขประจำตัวผู้เสียภาษี"
                value={formatIdCard(loadDetailData?.buyer_taxid)}
              />
            </Col>
            <Col xs={24} md={12}>
              <InfoRow
                label="โทรศัพท์"
                value={formatPhone(loadDetailData?.phone)}
              />
            </Col>
            <Col xs={24}>
              <InfoRow label="ที่อยู่" value={loadDetailData?.buyer_address} />
            </Col>
          </Row>

          {/* ─── Section 4: หมายเหตุ ─── */}
          {loadDetailData?.mark && (
            <>
              <Divider />
              <Title level={5} style={{ color: "#efa420" }}>
                📝 หมายเหตุ
              </Title>
              <div
                style={{
                  background: "#fffbe6",
                  border: "1px solid #ffe58f",
                  borderRadius: 8,
                  padding: 12,
                  whiteSpace: "pre-wrap",
                  fontSize: 13,
                  color: "#594300",
                }}
              >
                {loadDetailData?.mark}
              </div>
            </>
          )}
          {(roleId === "6" || roleId === "1") &&
            !record.TMBILL_DATE &&
            !saving && (
              <Col span={24} style={{ marginTop: "5px" }}>
                <Text strong>ประเภทการจ่าย</Text>
                <Select
                  value={paymentType} // ใช้ค่าจาก State
                  size="large"
                  style={{ width: "100%", marginTop: 8 }}
                  onChange={(val) => setPaymentType(val)} // อัปเดต State เมื่อมีการเลือกใหม่
                >
                  {getPAYCODE?.map((e) => {
                    return (
                      <Select.Option key={e.keyId} value={e.PAYCODE}>
                        {e.PAYCODE} ({e.PAYDESC})
                      </Select.Option>
                    );
                  })}
                </Select>
              </Col>
            )}
        </Card>
        {/* ปุ่ม */}
        <div style={{ marginTop: 20, textAlign: "right" }}>
          <Space>
            <Button
              danger
              onClick={() => {
                if (saving) {
                  console.log("saving", saving);
                  handleLoad?.();
                }
                handleClose();
              }}
              disabled={loading}
            >
              ปิด
            </Button>

            {(roleId === "1" || roleId === "6") &&
              !record.TMBILL &&
              !saving && (
                <Button
                  variant="outlined"
                  color="blue"
                  icon={<PrinterOutlined />}
                  loading={printing}
                  onClick={handleSave}
                >
                  กดรับเงิน
                </Button>
              )}

            {(saving || record.check_doc_ad || record.TMBILL_DATE) && (
              <Button
                icon={<PrinterOutlined />}
                loading={printing}
                onClick={handlePrint}
              >
                พิมพ์ใบเสร็จ
              </Button>
            )}
          </Space>
        </div>
      </Spin>
    </Modal>
  );
};

export default SellingCarImpound;
